import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeFamily, ThemeWithVersions } from "@/lib/api/types";
import { pickLocalized } from "@/lib/i18n/localized";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { Button, DataTable, ErrorState, ItemCell, LoadingState, TableEmpty, type Column } from "@/shared/rd";
import { useThemesQuery } from "../services/useThemesQuery";
import { getLatestPublishedVersionNumber } from "../services/theme-versions";
import { ThemeStatusBadge } from "../components/ThemeStatusBadge";
import { ThemeCreateForm } from "../components/ThemeCreateForm";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

type ThemesPageProps = {
  locale: Locale;
  /** The `themes/new` route: the list with the create drawer open over it. */
  createOpen?: boolean;
};

export function ThemesPage({ locale, createOpen = false }: ThemesPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useThemesQuery();
  const base = `/${locale}/admin/themes`;
  const page = t("themes.pageName");
  const number = (n: number) => n.toLocaleString(locale === "ar" ? "ar-EG" : "en-US");

  const columns: Array<Column<ThemeWithVersions>> = [
    {
      key: "name",
      label: t(keys.themes.listName),
      width: 2,
      render: (theme) => {
        const thumb = theme.versions.find((version) => version.thumbnailUrl)?.thumbnailUrl;
        return (
          <ItemCell
            badge={thumb ? undefined : "note"}
            media={
              thumb ? (
                <img
                  src={thumb}
                  alt=""
                  style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", flex: "none" }}
                />
              ) : undefined
            }
            name={pickLocalized(locale, theme.nameAr, theme.nameEn)}
            meta={
              <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                {theme.slug}
              </span>
            }
          />
        );
      },
    },
    {
      key: "family",
      label: t(keys.themes.family),
      width: 0.8,
      // Fall back to the raw family name when the catalog carries a family the
      // admin has no label key for (the seeded eo-* themes).
      render: (theme) => {
        const key = FAMILY_LABEL_KEY[theme.family];
        return key ? t(key) : theme.family;
      },
    },
    {
      key: "status",
      label: t(keys.common.status),
      width: 0.9,
      render: (theme) => <ThemeStatusBadge status={theme.status} />,
    },
    {
      key: "versions",
      label: t(keys.themes.versions),
      width: 0.7,
      align: "end",
      numeric: true,
      render: (theme) => number(theme.versions.length),
    },
    {
      key: "latestPublished",
      label: t(keys.themes.latestPublished),
      width: 1,
      align: "end",
      numeric: true,
      render: (theme) => {
        const latest = getLatestPublishedVersionNumber(theme.versions);
        return latest === null ? "—" : `v${latest}`;
      },
    },
    {
      key: "sortWeight",
      label: t(keys.themes.sortWeight),
      width: 0.8,
      align: "end",
      numeric: true,
      render: (theme) => <span style={{ color: "var(--rd-text-secondary)" }}>{number(theme.sortWeight)}</span>,
    },
  ];

  return (
    <>
      <PageHeaderActions>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate(`${base}/new`)}>
          {t(keys.themes.create)}
        </Button>
      </PageHeaderActions>

      {isLoading ? (
        <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} />
      ) : error || !data ? (
        <ErrorState
          title={t(keys.shell.errorTitle, { page })}
          sub={t(keys.shell.errorSub)}
          retryLabel={t(keys.shell.retry)}
          onRetry={() => void refetch()}
        />
      ) : (
        <DataTable
          label={t(keys.themes.title)}
          columns={columns}
          rows={data}
          rowKey={(theme) => theme.id}
          minWidth={760}
          onRowClick={(theme) => navigate(`${base}/${theme.id}`)}
          empty={
            <TableEmpty
              illustration="admin-empty-table"
              title={t("themes.emptyTitle")}
              actions={
                <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate(`${base}/new`)}>
                  {t(keys.themes.create)}
                </Button>
              }
            >
              {t("themes.emptySub")}
            </TableEmpty>
          }
        />
      )}

      {createOpen ? <ThemeCreateForm locale={locale} onClose={() => navigate(base)} /> : null}
    </>
  );
}
